import cn from 'classnames';
import { useMemo } from 'react';
import { Card, Icon, InfinityLoading, withErrorBoundary } from 'ui';
import { useFeedInfiniteQuery } from '../queries';
import { PinnedCardRenderer } from './pinned-card-renderer';
import { PinnedContentObjectCard } from './pinned-content-object-card';

type P = {
  // TODO: #87927 cover is the old pinned card, used by the home feed and the old community feed. content is the new card. Delete cardLayout and PinnedContentObjectCard when those feeds use content.
  cardLayout?: 'content' | 'cover';
  communityId?: string;
};

const PinnedFeed = ({ cardLayout = 'cover', communityId }: P) => {
  const { data, fetchNextPage, hasNextPage, isFetching } = useFeedInfiniteQuery({
    communityId,
    pagination: { pageSize: 3 },
    pinned: true,
  });

  const scrollItems = useMemo(() => {
    const items = (data?.pages.flat() ?? []).filter((item) => {
      if (item.type === 'event') {
        console.warn('[App] Trying to render object of `event` type in pinned feed.');
        return false;
      }

      return true;
    });

    if (items.length === 0) {
      return null;
    }

    return [
      ...items.map((item) => ({
        children:
          cardLayout === 'content' ? <PinnedCardRenderer item={item} /> : <PinnedContentObjectCard item={item} />,
        key: item.id,
      })),
      ...(isFetching || hasNextPage
        ? [
            {
              children: (
                <div className="pinned-feed__loader">
                  <InfinityLoading hasMoreData={hasNextPage} isLoading={isFetching} onEnter={fetchNextPage} />
                </div>
              ),
              key: 'loader',
            },
          ]
        : []),
    ];
  }, [cardLayout, data, fetchNextPage, hasNextPage, isFetching]);

  if (!scrollItems) {
    return null;
  }

  return (
    <Card marginBottom={false} title={<Card.Title icon={<Icon icon="pin-2-line" />}>Pinned posts</Card.Title>}>
      <Card.HorizontalScroll
        className={cn({ 'pinned-feed__scroller--cover': cardLayout === 'cover' })}
        gap="ms"
        items={scrollItems}
        span="auto"
      />
    </Card>
  );
};

export default withErrorBoundary(PinnedFeed, { message: 'Failed to load pinned posts.' });
